import { useCreateCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import { useListEquipmentTypes } from "#src/components/hooks/adapters/useEquipment";
import { PickListFields } from "#src/routes/forms/categories/[categoryId]/create-form-template/steps/CreateFormTemplateSectionsStep/CreateFormTemplateAddQuestionDrawer/PickListFields";
import {
  Button,
  Dialog,
  DropdownInput,
  Form,
  RadioInput,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  AttributeDataType,
  CustomAttributeSchema,
} from "@validereinc/domain";
import { Assets } from "@validereinc/domain-controllers";
import { toTitleCaseString } from "@validereinc/utilities";
import { snakeCase } from "es-toolkit/compat";
import React, { useEffect } from "react";
import { FlowTypeDropdownInput } from "../Dropdowns/FlowTypeDropdownInput";
import { useGetAssetTypeOptions } from "../FilterAreas/assetFilters.helpers";

const { AssetTypeSelection } = Assets.view;

export const AddCustomAttributeDialog = ({
  isOpen,
  onClose,
  assetType,
}: {
  isOpen: boolean;
  onClose: () => void;
  assetType: AssetTypeType;
}) => {
  const form = useForm({});
  const equipmentTypes = useListEquipmentTypes({}).data?.data ?? [];
  const assetTypeOptions = useGetAssetTypeOptions().filter(
    (option) => option.value !== AssetTypeSelection.ALL_ASSETS
  );

  const onSuccess = () => {
    onClose?.();
  };

  const isPicklist = [
    AttributeDataType.PICK_LIST,
    AttributeDataType.MULTI_PICK_LIST,
  ].includes(form.watch("data_type"));
  const isLookup = form.watch("data_type") == AttributeDataType.LOOKUP;
  const isNumber = form.watch("data_type") == AttributeDataType.NUMBER;

  const createCustomAttribute = useCreateCustomAttributes({ onSuccess });
  const handleSubmit = form.handleSubmit((values) => {
    const {
      display_name,
      description,
      data_type,
      pick_list_values,
      lookup_entity_type,
      is_required,
      entity_subtypes,
    } = values;
    const data: any = {
      entity_type: assetType,
      field_name: `${snakeCase(display_name)}`,
      display_name,
      description,
      is_required,
      data_type,
      lookup_entity_type,
      pick_list_values,
    };

    if (
      entity_subtypes !== undefined &&
      entity_subtypes !== null &&
      Array.isArray(entity_subtypes) &&
      entity_subtypes.length !== 0
    ) {
      data.entity_subtypes = entity_subtypes;
    }

    createCustomAttribute.mutate({ data });
  });

  useEffect(() => {
    form.reset();
  }, [isOpen]);

  const dataTypeOptions = Object.entries(AttributeDataType).map(
    ([key, value]) => ({
      label: toTitleCaseString(value, { intent: "ui" }),
      name: value,
      id: key,
    })
  );

  return (
    <Dialog
      title="Add Custom Attribute"
      isOpen={isOpen}
      onClose={onClose}
      actionRow={[
        <Button
          key="add-custom-attribute"
          variant="primary"
          onClick={handleSubmit}
        >
          Add
        </Button>,
      ]}
    >
      <Form {...form}>
        <TextInput
          name={CustomAttributeSchema.keyof().Enum.display_name}
          label="Display Name"
          isRequired
          type="text"
        />
        <TextInput
          name={CustomAttributeSchema.keyof().Enum.description}
          label="Description"
          type="text"
        />
        {assetType === AssetType.EQUIPMENT ? (
          <DropdownInput
            label="Equipment Type"
            name={CustomAttributeSchema.keyof().Enum.entity_subtypes}
            options={equipmentTypes}
            isSortedAlphabetically
            labelKey="name"
            valueKey="id"
            isMulti
            isFluid
            isSearchable
            isOptionalTextShown
          />
        ) : null}

        {assetType === AssetType.FLOW ? (
          <FlowTypeDropdownInput
            name={CustomAttributeSchema.keyof().Enum.entity_subtypes}
            label="Flow Type"
            isRequired={false}
            isOptionalTextShown={true}
            description="If no flow type is specified the custom attribute will be applicable for all flow types."
          />
        ) : null}
        <RadioInput
          name={CustomAttributeSchema.keyof().Enum.is_required}
          label="Is Required"
          isRequired
          options={[
            {
              label: "Yes",
              value: true,
            },
            {
              label: "No",
              value: false,
            },
          ]}
          valueKey="value"
          labelKey="label"
        />
        <DropdownInput
          name={CustomAttributeSchema.keyof().Enum.data_type}
          label="Data Type"
          options={dataTypeOptions}
          valueKey="name"
          labelKey="label"
          isRequired
        />
        {isPicklist ? <PickListFields /> : null}
        {isLookup ? (
          <DropdownInput
            name={CustomAttributeSchema.keyof().Enum.lookup_entity_type}
            label="Lookup Asset"
            options={assetTypeOptions}
            valueKey="value"
            labelKey="label"
            isRequired
          />
        ) : null}
        {isNumber ? (
          <TextInput
            name={CustomAttributeSchema.keyof().Enum.unit}
            label="Unit"
            isRequired
            type="text"
          />
        ) : null}
      </Form>
    </Dialog>
  );
};
